<template>
  <b-modal
    id="news-popup-general-settings"
    title="General Settings"
    size="lg"
    centered
    footer-class="border-top pt-3 justify-content-between"
    @hidden="clearModal"
    @shown="initModal">
    <template slot="modal-title">
      General Settings 
    </template>
    <b-form>
    <template v-if="loading">
      <div class="text-center py-5">Loading...</div>
    </template>
    <template v-else>
      <b-row no-gutters>
        <b-col sm="12" md="3" class="pr-1">
          <h6 class="main-label">Sequential Posting</h6>
        </b-col>
        <b-col>
          <div style="margin-top: -20px;">
            <switches
              theme="custom"
              color="primary"
              type-bold="true"
              text-enabled="On"
              text-disabled="Off"
              v-model="generalSettings.is_sequential_posting"
            ></switches>
          </div>
          <b-row>
            <b-col cols="9">
              <div id="helper" class="bg-light p-2 rounded d-flex">
                <img src="@/assets/images/info.svg" alt="Info" class="mr-2">
                If toggle off, published banners will be posted randomly.
              </div>
            </b-col>
          </b-row>
          <div
            id="sequential-banners"
            class="mt-3"
            v-if="generalSettings.is_sequential_posting">
            <p class="section-header">Sequence arrangement of published banners</p>
            <div id="sequential-banners-cards-container">
              <template v-if="loading_published_banners">
                <div class="bg-light rounded text-center py-2">Loading published banners...</div>
              </template>
              <template v-else-if="!loading_published_banners && publishedBanners.length === 0">
                <div class="bg-light rounded text-center py-2">No published banners...</div>
              </template>
              <draggable
                :list="publishedBanners"
                draggable=".banner-card"
                handle=".banner-handle"
                ghostClass="draggable-ghost"
                dragClass="draggable-ghost"
                v-else
              >
                <div
                  class="banner-card"
                  v-for="(banner, index) in publishedBanners"
                  :key="`published_banners_${index}`">
                  <b-card
                    class="border-0 mb-3"
                    body-class="bg-white card-shadow rounded position-relative">
                    <img src="@/assets/images/banner-handle.svg" alt="move" class="banner-handle position-absolute">
                    <div class="d-flex align-items-center">
                      <div class="img-wrapper rounded">
                        <img :src="banner.image" :alt="banner.title" :class="[`img-fluid -${banner.type}`]">
                      </div>
                      <div class="banner-details ml-0 ml-md-3">
                        <h5>{{ banner.title }}</h5>
                        <p class="text-muted mb-0"> Poster Type: <span class="text-capitalize">{{ banner.type }}</span></p>
                      </div>
                    </div>
                  </b-card>
                </div>
              </draggable>
            </div>
          </div>
          <b-form-group>
            <b-form-input type="text" hidden :state="isValid('pop_up_uuid')"></b-form-input>
            <b-form-invalid-feedback :state="isValid('pop_up_uuid')">
              {{ errorMessage('pop_up_uuid') }}
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row class="mb-2">
        <b-col>
          <hr class="form-divider">
        </b-col>
      </b-row>
      <b-row>
        <b-col sm="12" md="3">
          <h6 class="main-label">Recurrence</h6>
        </b-col>
        <b-col>
          <b-form-group label="Frequency">
            <b-row>
              <b-col cols="3">
                <b-form-input
                  type="number"
                  v-model="generalSettings.recurrence_value"
                  min="1"
                  max="10"
                  class="frequency-value"
                  maxlength="2">
                </b-form-input>
              </b-col>
              <b-col cols="5" class="d-inline-flex align-items-center">
                / Day
              </b-col>
              <b-col cols="12">
                <b-form-input type="text" hidden :state="isValid('recurrence_value')"></b-form-input>
                <b-form-invalid-feedback :state="isValid('recurrence_value')">
                  {{ errorMessage("recurrence_value") }}
                </b-form-invalid-feedback>
              </b-col>
            </b-row>
          </b-form-group>
          <b-form-group class="mb-0">
            Week
            <switches
              theme="custom"
              color="primary"
              type-bold="true"
              text-enabled="On"
              text-disabled="Off"
              v-model="generalSettings.is_recurrence_weeks"
              style="top: -20px; left: 10px;"
              :emit-on-mount="false"
              @input="resetRecurrenceDays('weeks')"
            ></switches>
          </b-form-group>
          <b-form-group
            label="Repeat On"
            v-if="generalSettings.is_recurrence_weeks"
            style="margin-top: -20px;">
            <b-checkbox-group
              id="repeat-on-wrapper"
              v-model="generalSettings.recurrence_days">
              <b-form-checkbox
                v-for="(option, index) in repeatOptions"
                :key="`repeat_every_${index}`"
                :value="option.value"
                class="px-0">
                {{ option.text }}
              </b-form-checkbox>
            </b-checkbox-group>
            <b-form-input type="text" hidden :state="isValid('recurrence_days')"></b-form-input>
            <b-form-invalid-feedback :state="isValid('recurrence_days')">
              {{ errorMessage('recurrence_days') }}
            </b-form-invalid-feedback>
          </b-form-group>
          <b-form-group
            class="mb-0"
            :style="{
              'margin-top': !generalSettings.is_recurrence_weeks ? '-20px' : null
            }">
            Month
            <switches
              theme="custom"
              color="primary"
              type-bold="true"
              text-enabled="On"
              text-disabled="Off"
              v-model="generalSettings.is_recurrence_months"
              style="top: -20px; left: 10px;"
              :emit-on-mount="false"
              @input="resetRecurrenceDays('months')"
            ></switches>
          </b-form-group>
          <b-form-group
            label="Repeat On (Choose days of the month)"
            v-if="generalSettings.is_recurrence_months"
            style="margin-top: -20px;">
            <b-checkbox-group
              id="repeat-on-wrapper"
              v-model="generalSettings.recurrence_days">
              <b-form-checkbox
                v-for="(option, index) in daysOfTheMonth"
                :key="`repeat_every_${index}`"
                :value="option.value"
                class="px-0">
                {{ option.text }}
              </b-form-checkbox>
            </b-checkbox-group>
            <b-form-input type="text" hidden :state="isValid('recurrence_days')"></b-form-input>
            <b-form-invalid-feedback :state="isValid('recurrence_days')">
              {{ errorMessage('recurrence_days') }}
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
      </b-row>
    </template>
    </b-form>
    <template slot="modal-footer">
      <b-button
        pill
        variant="ghost"
        class="mr-1 btn-cancel"
        :disabled="loading || saving"
        @click="$root.$emit('bv::hide::modal', 'news-popup-general-settings')">
        Cancel
      </b-button>
      <b-button
        variant="primary" class="ml-1"
        :disabled="loading || saving"
        @click="checkGeneralSettingsFields">
        {{ saving ? 'Saving...' : 'Save' }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import { mapFields } from "vuex-map-fields";
import { mapActions, mapGetters, mapMutations } from "vuex";
import draggable from "vuedraggable";
import Switches from "vue-switches";

export default {
    name: "NewsAndPopUpGeneralSettings",
    components: {
        Switches,
        draggable
    },
    data() {
        return {
            recurrenceOptions: [
                { value: "day", text: "Day(s)" },
                { value: "week", text: "Week(s)" },
                { value: "month", text: "Month(s)" }
            ],
            repeatOptions: [
                { value: "sun", text: "S" },
                { value: "mon", text: "M" },
                { value: "tue", text: "T" },
                { value: "wed", text: "W" },
                { value: "thu", text: "T" },
                { value: "fri", text: "F" },
                { value: "sat", text: "S" }
            ],
            publishedBanners: [
                {
                    img: require("@/assets/images/246x246.png"),
                    name: "Christmas Promo Poster",
                    type: "poster"
                },
                {
                    img: require("@/assets/images/246x2461.png"),
                    name: "Buy 1 Take 1 Promo Poster",
                    type: "banner"
                }
            ],
            loading_published_banners: false
        };
    },
    computed: {
        ...mapFields("designs", ["generalSettings"]),
        ...mapGetters({
            errors: "designs/errors",
            loading: "designs/loading",
            publishedPopUps: "designs/popUps",
            saving: "designs/saving"
        }),
        daysOfTheMonth() {
            let days = [];
            let day = 1;
            while (day < 32) {
                days.push({ value: day, text: day });
                day++;
            }
            return days;
        }
    },
    methods: {
        ...mapActions({
            getGeneralSettings: "designs/getGeneralSettings",
            saveGeneralSettings: "designs/saveGeneralSettings",
            getPublishedPopUps: "designs/getNewsPopUp"
        }),
        ...mapMutations({
            setErrors: "designs/errors"
        }),
        async initModal() {
            await this.getGeneralSettings();
            this.loading_published_banners = true;
            await this.getPublishedPopUps("saved").then(() => {
                const sequentials = this.generalSettings.sequentials;

                this.publishedBanners =
                    sequentials.length > 0
                        ? sequentials
                              .map(popup => {
                                  return this.publishedPopUps.find(
                                      published =>
                                          published.uuid === popup.pop_up_uuid
                                  );
                              })
                              .filter(popup => popup)
                        : this.publishedPopUps;

                this.loading_published_banners = false;
            });
        },
        clearModal() {
            this.generalSettings = {
                is_sequential_posting: false,
                recurrence_days: [],
                recurrence_type: "day",
                recurrence_value: 1,
                sequentials: [],
                is_recurrence_weeks: false,
                is_recurrence_months: false,
                id: null
            };
            this.setErrors(null);
        },
        checkGeneralSettingsFields() {
            const form = this.generalSettings;
            const errors = {};

            if (!form.recurrence_value)
                errors["recurrence_value"] = "Frequency value is required.";
            else if (
                Number(form.recurrence_value) < 1 ||
                Number(form.recurrence_value) > 10
            )
                errors["recurrence_value"] = "Frequency value must be 1 - 10.";

            if (
                (form.is_recurrence_weeks || form.is_recurrence_months) &&
                form.recurrence_days.length === 0
            )
                errors["recurrence_days"] = "Recurrency days is required.";

            this.$nextTick(() => {
                if (Object.keys(errors).length > 0) this.setErrors(errors);
                else this.handleSaveGeneralSettings();
            });
        },
        async handleSaveGeneralSettings() {
            const param = this.generalSettings;

            if (this.generalSettings.is_sequential_posting)
                param["pop_up_uuid"] = this.publishedBanners.map(
                    popup => popup.uuid
                );
            else param["pop_up_uuid"] = [];

            delete param["sequentials"];

            await this.saveGeneralSettings(param).then(() => {
                if (
                    this.errors === null ||
                    Object.keys(this.errors).length === 0
                )
                    this.$root.$emit(
                        "bv::hide::modal",
                        "news-popup-general-settings"
                    );
            });
        },
        isValid(field) {
            if (this.errors == null) {
                return false;
            }
            return this.errors[field] ? false : true;
        },
        errorMessage(field) {
            if (this.errors == null) {
                return "";
            }

            return this.errors[field];
        },
        resetRecurrenceDays(source) {
            switch (source) {
                case "weeks":
                    this.generalSettings.is_recurrence_months = false;
                    break;
                case "months":
                    this.generalSettings.is_recurrence_weeks = false;
            }
            this.generalSettings.recurrence_days = [];
            if (this.errors && "recurrence_days" in this.errors)
                delete this.errors["recurrence_days"];
        }
    }
};
</script>

<style lang="scss" scoped>
#helper {
    font-size: 12px;
    color: #49545c;
}
#sequential-banners {
    .section-header {
        font-family: MainFontRegular, sans-serif;
        font-size: 14px;
        color: #2a2a2a;
    }
}
#sequential-banners-cards-container {
    .card-shadow {
        box-shadow: 0px 15px 40px rgba(178, 186, 202, 0.4);
    }
    .banner-handle {
        right: 10px;
        top: 10px;
        cursor: pointer;
    }
    .img-wrapper {
        width: 70px;
        height: 70px;
        overflow: hidden;
        img {
            &.-banner {
                width: 100%;
                height: 100%;
                object-fit: cover;
            }
        }
    }
    .banner-details {
        font-family: MainFontRegular, sans-serif;
        h5 {
            font-size: 14px;
            font-family: MainFontBold, sans-serif;
            font-weight: normal;
        }
        p {
            font-size: 12px;
        }
    }
}
</style>

<style lang="scss">
@import url("https://fonts.googleapis.com/css2?family=Mulish:wght@400;500;700&display=swap");
#news-popup-general-settings .modal-content {
    padding: 0;
}
#repeat-on-wrapper {
    .custom-control.custom-checkbox {
        & > .custom-control-label {
            &::before,
            &::after {
                display: none;
            }
            width: 40px;
            height: 40px;
            border-radius: 20px;
            border: 1px solid #e2e2e2;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            font-size: 14px;
            font-family: MainFontBold, sans-serif;
            font-weight: normal;
            color: #2f3941;
        }
        & > .custom-control-input:checked + .custom-control-label {
            background-color: #ff8a00;
            border-color: #ff8a00;
            color: white;
        }
    }
}
</style>
